import { useState, useEffect } from 'react';
import { Music, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ExampleWork {
  id: string;
  title: string;
  file_url: string;
  file_size_bytes: number;
  duration_seconds: number | null;
  display_order: number;
}

interface MentorExampleWorksDisplayProps {
  mentorId: string;
}

export function MentorExampleWorksDisplay({ mentorId }: MentorExampleWorksDisplayProps) {
  const [works, setWorks] = useState<ExampleWork[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWorks();
  }, [mentorId]);

  const fetchWorks = async () => {
    try {
      const { data, error } = await supabase
        .from('mentor_example_works')
        .select('*')
        .eq('mentor_id', mentorId)
        .order('display_order');
      
      if (error) throw error;
      setWorks(data || []);
    } catch (error) {
      console.error('Error fetching example works:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (works.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 pt-6 border-t">
      <h3 className="text-xl font-bold text-glow mb-4">Example Works</h3>
      <div className="space-y-4">
        {works.map((work) => (
          <div key={work.id} className="space-y-2">
            <div className="flex items-center gap-2">
              <Music className="w-4 h-4 text-primary" />
              <h4 className="font-medium">{work.title}</h4>
            </div>
            <audio
              controls
              controlsList="nodownload noplaybackrate"
              className="w-full"
              preload="metadata"
              onContextMenu={(e) => e.preventDefault()}
            >
              <source src={work.file_url} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          </div>
        ))}
      </div>
    </div>
  );
}
